/**
 * Where a tooltip goes, from its measured size: beside the pointer it follows, or on one side of the
 * control it describes (keyboard focus, a touch pin). It flips when that side has no room, stays a
 * margin inside the viewport and is never taller than the viewport (the box clips to `maxHeight`).
 */
export interface TooltipSize { width: number; height: number }
export interface TooltipRect { left: number; top: number; right: number; bottom: number }
export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';
export type TooltipAt =
  | { pointer: { x: number; y: number }; anchor?: undefined }
  | { anchor: TooltipRect; side?: TooltipSide; pointer?: undefined };
export interface TooltipPlace { left: number; top: number; maxHeight: number }

const MARGIN = 10;
const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(value, high));

export function placeTooltip(at: TooltipAt, box: TooltipSize, view: TooltipSize): TooltipPlace {
  const maxHeight = view.height - 2 * MARGIN;
  const height = Math.min(box.height, maxHeight);
  const inView = (left: number, top: number): TooltipPlace => ({
    left: clamp(left, MARGIN, Math.max(MARGIN, view.width - MARGIN - box.width)),
    top: clamp(top, MARGIN, Math.max(MARGIN, view.height - MARGIN - height)),
    maxHeight,
  });

  if (at.pointer) {
    const { x, y } = at.pointer;
    const above = y - height - MARGIN;
    return inView(x + MARGIN + box.width > view.width ? x - box.width - MARGIN : x + MARGIN, above < 0 ? y + MARGIN : above);
  }

  const { anchor, side = 'top' } = at;
  const above = anchor.top - height - MARGIN;
  const below = anchor.bottom + MARGIN;
  const centred = anchor.left + (anchor.right - anchor.left - box.width) / 2;
  if (side === 'left') return inView(anchor.left - box.width - MARGIN, anchor.top);
  if (side === 'right') return inView(anchor.right + MARGIN, anchor.top);
  if (side === 'bottom') return inView(centred, below + height > view.height - MARGIN ? above : below);
  return inView(centred, above < MARGIN ? below : above);
}
